/**
 * PlayerPreviewBreakdownModalView — layers a single KPI breakdown on top of the
 * player preview modal (SE-5473).
 *
 * The breakdown modals (Gemini Player Valuation, GPR, style fits) render via a
 * GorhomPortal into a PLAYER_DETAIL_PORTAL host that only exists on the full
 * player page / breakdown route — not inside the preview modal, which is an RN
 * Modal opened from Global Search. Mounting the breakdown screen (which provides
 * its own host) inside a transparent, full-screen Modal shell lets it escape the
 * preview modal's ScrollView and stack on top. Closing it returns the user to
 * the preview modal underneath instead of tearing the whole overlay down.
 * @remarks This is a View component — contains no business logic, hooks, or state.
 */

import { memo } from "react";

import { Modal, ModalContent } from "@/components/atoms/Modal";
import { Main as PlayerMetricBreakdown } from "@/features/player-metric-breakdown";

/**
 * Props for PlayerPreviewBreakdownModalView.
 */
export interface PlayerPreviewBreakdownModalViewProps {
  /** Breakdown metric slug to render. */
  readonly slug: string;
  /** Player id whose breakdown to render. */
  readonly playerId: string;
  /** Close handler — returns the user to the preview modal underneath. */
  readonly onClose: () => void;
}

/**
 * Renders the layered breakdown modal.
 * @param root0 - Component props
 * @param root0.slug - Breakdown metric slug to render
 * @param root0.playerId - Player id whose breakdown to render
 * @param root0.onClose - Close handler returning to the preview modal
 * @returns The layered breakdown modal
 */
const PlayerPreviewBreakdownModalView = ({
  slug,
  playerId,
  onClose,
}: PlayerPreviewBreakdownModalViewProps) => (
  <Modal isOpen onClose={onClose} size="full">
    <ModalContent className="flex-1 border-0 bg-transparent p-0 shadow-none">
      <PlayerMetricBreakdown
        playerId={playerId}
        metricSlug={slug}
        onClose={onClose}
      />
    </ModalContent>
  </Modal>
);

PlayerPreviewBreakdownModalView.displayName = "PlayerPreviewBreakdownModalView";

export default memo(PlayerPreviewBreakdownModalView);
